/**
 * Agregación simple para tabla dinámica de leads.
 * Cohorte = fecha de creación; agrupa por etapa actual de Kommo o estado
 * interno GI Capital (Sales_Internal_States / Sales_Stage_Map, no históricos).
 * Funciones puras, sin acceso a servicios.
 */
export const CHILE_TZ = 'America/Santiago';

export function chileYmd(value) {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value);
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: CHILE_TZ, year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(date);
  const at = (type) => parts.find((p) => p.type === type)?.value || '';
  return at('year') + '-' + at('month') + '-' + at('day');
}

export function offsetYmd(ymd, days) {
  const d = new Date(ymd + 'T12:00:00Z');
  if (!Number.isFinite(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

// Límite técnico de fechas que acepta la API GAS/Google Sheets.
// Se envía un rango explícito porque omitir las fechas en GAS
// aplicaría silenciosamente el mes actual.
export const ALL_LEADS_DATE_FROM = '0100-01-01';
export const ALL_LEADS_DATE_TO = '9999-12-31';

export function rangeForPreset(preset, from, to, now = new Date()) {
  if (preset === 'all') {
    return { dateFrom: ALL_LEADS_DATE_FROM, dateTo: ALL_LEADS_DATE_TO };
  }
  const end = chileYmd(now);
  if (preset === 'custom') {
    return /^\d{4}-\d{2}-\d{2}$/.test(from || '') &&
      /^\d{4}-\d{2}-\d{2}$/.test(to || '') && from <= to
      ? { dateFrom: from, dateTo: to } : null;
  }
  const days = preset === '90' ? 90 : preset === '30' ? 30 : 7;
  return { dateFrom: offsetYmd(end, 1 - days), dateTo: end };
}

export function stageForLead(lead) {
  const pipeline = String(lead?.pipelineName || lead?.pipelineId || '').trim();
  const stage = String(lead?.stageName || lead?.stageId || '').trim();
  const id = String(lead?.pipelineId || pipeline) + '::' + String(lead?.stageId || stage);
  return {
    id,
    name: stage,
    label: stage ? stage + (pipeline ? ' · ' + pipeline : '') : 'Sin etapa Kommo'
  };
}

/**
 * Cada estado GI es una entidad administrable, identificada por stateId:
 * nunca agrupar por nombre (el usuario puede renombrarlo en Configuración).
 * La clasificación recibida ya fue resuelta por el GAS con Sales_Stage_Map.
 */
export const GI_UNCLASSIFIED_KEY = 'gi:__sin_clasificar__';

export function giStateForLead(lead, catalog = []) {
  const stateId = !lead?.unclassified ? String(lead?.internalStateId || '').trim() : '';
  if (!stateId) {
    return {
      id: GI_UNCLASSIFIED_KEY,
      name: 'Sin clasificar',
      label: 'Sin clasificar',
      order: Number.MAX_SAFE_INTEGER
    };
  }
  const state = (Array.isArray(catalog) ? catalog : []).find(
    (s) => String(s?.stateId || '').trim() === stateId
  );
  const name = String(state?.name || lead?.internalStateName || stateId).trim() || stateId;
  const order = state?.order == null || !Number.isFinite(Number(state.order))
    ? 999
    : Number(state.order);
  return { id: 'gi:' + stateId, name, label: name, order, stateId };
}

export function pivotLeadsByGI(leads, range, group = 'day', states = [], selectedIds = null) {
  const resolved = pivotLeads(
    leads, range, group, '', selectedIds, (lead) => giStateForLead(lead, states)
  );
  return {
    ...resolved,
    stages: resolved.stages.sort((a, b) =>
      a.order - b.order || String(a.label).localeCompare(String(b.label), 'es')
    )
  };
}

/**
 * Etiquetas del espejo de Kommo (columna "Etiquetas").
 * Cada lead puede tener varias; comparación sin distinción de mayúsculas.
 */
export function leadTags(lead) {
  return [...new Map(
    String(lead?.tagsText || '')
      .split(/[;,|]/)
      .map((tag) => tag.trim().replace(/\s+/g, ' '))
      .filter(Boolean)
      .map((tag) => [tag.toLocaleLowerCase('es-CL'), tag])
  ).values()];
}

export function tagCatalogForLeads(leads) {
  const tags = new Map();
  const uniqueLeads = new Set();
  for (const lead of Array.isArray(leads) ? leads : []) {
    const id = String(lead?.leadId || '');
    if (!id || uniqueLeads.has(id)) continue;
    uniqueLeads.add(id);
    for (const name of leadTags(lead)) {
      const key = name.toLocaleLowerCase('es-CL');
      const item = tags.get(key) || { key, name, count: 0 };
      item.count += 1;
      tags.set(key, item);
    }
  }
  return [...tags.values()].sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

/** Null = sin filtro; selección vacía = cero leads; varias etiquetas = OR. */
export function filterLeadsByTags(leads, selectedTagKeys = null) {
  if (selectedTagKeys === null) return Array.isArray(leads) ? leads : [];
  const chosen = selectedTagKeys instanceof Set ? selectedTagKeys : new Set(selectedTagKeys);
  return (Array.isArray(leads) ? leads : []).filter((lead) =>
    leadTags(lead).some((name) => chosen.has(name.toLocaleLowerCase('es-CL')))
  );
}

/** La etapa Personal es administrativa y no debe influir en el dashboard por defecto. */
export function stageVisibleByDefault(stage) {
  const name = String(stage?.name || '').trim().toLocaleLowerCase('es-CL');
  return name !== 'personal' && name !== 'persona';
}

/** Los cambios explícitos del usuario tienen prioridad sobre la selección inicial. */
export function stageIsSelected(stage, overrides = {}) {
  return Object.prototype.hasOwnProperty.call(overrides, stage.id)
    ? overrides[stage.id] === true
    : stageVisibleByDefault(stage);
}

export function dateGroup(ymd, group) {
  if (group === 'month') return ymd.slice(0, 7);
  if (group !== 'week') return ymd;
  const d = new Date(ymd + 'T12:00:00Z');
  const day = d.getUTCDay();
  return offsetYmd(ymd, -(day === 0 ? 6 : day - 1));
}

export function pivotLeads(leads, range, group = 'day', pipelineId = '', selectedStageIds = null, resolveStage = stageForLead) {
  const byStage = new Map();
  const byDate = new Map();
  const unique = new Set();
  const included = [];
  for (const lead of Array.isArray(leads) ? leads : []) {
    const id = String(lead?.leadId || '');
    if (!id || unique.has(id)) continue;
    unique.add(id);
    const ymd = chileYmd(lead.createdAtIso);
    if (!ymd || !range || ymd < range.dateFrom || ymd > range.dateTo) continue;
    if (pipelineId && String(lead.pipelineId || '') !== pipelineId) continue;
    const date = dateGroup(ymd, group);
    const stage = resolveStage(lead);
    if (selectedStageIds !== null && !selectedStageIds.has(stage.id)) continue;
    const sr = byStage.get(stage.id) || { ...stage, count: 0 };
    sr.count++;
    byStage.set(stage.id, sr);
    const dr = byDate.get(date) || { date, total: 0, stages: {} };
    dr.total++;
    dr.stages[stage.id] = (dr.stages[stage.id] || 0) + 1;
    byDate.set(date, dr);
    included.push({ ...lead, _dateGroup: date, _stageKey: stage.id });
  }
  const stages = [...byStage.values()].sort((a, b) =>
    b.count - a.count || a.label.localeCompare(b.label, 'es')
  );
  const rows = [...byDate.values()].sort((a, b) => b.date.localeCompare(a.date));
  return { rows, stages, leads: included, total: included.length };
}

/**
 * Orden oficial de Kommo: cada embudo contiene sus etapas ordenadas por "sort".
 * Coincidencia estricta por ID de embudo + ID de etapa para no mezclar flujos.
 * Etapas sin mapeo se mantienen al final (en el orden recibido).
 *
 * @param {Array<{id:string}>} stages
 * @param {Array<{id:string, statuses:Array<{id:string,sort:number}>}>} pipelines
 */
export function sortStagesByKommoOrder(stages, pipelines) {
  const positions = new Map();
  let seq = 0;
  for (const pipe of Array.isArray(pipelines) ? pipelines : []) {
    const pipeId = String(pipe?.id ?? '').trim();
    if (!pipeId) continue;
    const statuses = Array.isArray(pipe.statuses) ? pipe.statuses : [];
    // El endpoint existente ya las ordena, pero usar sort explícitamente
    // permite comprobar el mismo comportamiento con cualquier fuente.
    const ordered = [...statuses].sort((a, b) => {
      const aSort = Number(a?.sort);
      const bSort = Number(b?.sort);
      const va = Number.isFinite(aSort) ? aSort : Number.MAX_SAFE_INTEGER;
      const vb = Number.isFinite(bSort) ? bSort : Number.MAX_SAFE_INTEGER;
      return va - vb;
    });
    for (const stage of ordered) {
      const stageId = String(stage?.id ?? '').trim();
      if (!stageId) continue;
      const key = pipeId + '::' + stageId;
      if (!positions.has(key)) positions.set(key, seq++);
    }
  }
  return [...(Array.isArray(stages) ? stages : [])].sort((a, b) => {
    const posA = positions.get(a.id);
    const posB = positions.get(b.id);
    if (posA == null && posB == null) return 0;
    if (posA == null) return 1;
    if (posB == null) return -1;
    return posA - posB;
  });
}
